export const SITE_NAME = "SG Office Supplies";
export const SITE_LEGAL_NAME = "SG Office Supplies LLC";
export const SITE_TAGLINE =
  "Office stationery, school supplies, printing & branding across the UAE since 2010.";

export const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://www.sgoffice.me");

export const DEFAULT_OG_IMAGE = "/favicon.png";

export const CONTACT = {
  phone: "+971509348007",
  email: "sales@sgoffice.me",
  address: {
    street: "Warehouse 36, 4B St, next to Easy Truck Warehouse, Al Quoz",
    locality: "Dubai",
    country: "AE",
  },
};

export const SOCIAL_LINKS = [
  "https://www.facebook.com/Stationeryeproduct/",
  "https://www.instagram.com/sgofficesupplies.2022/",
  "https://www.linkedin.com/company/sg-office-supplies-limited/",
];

/** Static page SEO definitions */
export const PAGE_SEO = {
  home: {
    title: "Office & School Supplies UAE | SG Office Supplies Dubai",
    description:
      "SG Office Supplies — trusted since 2010 for office stationery, school essentials, printing & branding across the UAE. Shop online or request a bulk quote.",
    path: "/",
  },
  shop: {
    title: "Shop Office & School Supplies Online | SG Office UAE",
    description:
      "Browse files, folders, writing instruments, office equipment, arts & crafts, and more. Order online with delivery across the United Arab Emirates.",
    path: "/products",
  },
  about: {
    title: "About SG Office Supplies | Office Stationery Supplier Dubai Since 2010",
    description:
      "Learn about SG Office Supplies LLC — your UAE partner for office stationery, educational products, printing solutions, and workspace essentials since 2010.",
    path: "/about",
  },
  signGraphics: {
    title: "Signage & Graphics Dubai | Exhibition & Retail Displays | SG Office",
    description:
      "Professional signage, acrylic fabrication, light boxes, trade show displays, and event branding in Dubai & UAE. Contact SG Sign and Graphics for custom solutions.",
    path: "/sign-graphics",
  },
  catalogue: {
    title: "Product Catalogues Download | Office & School Supplies | SG Office",
    description:
      "Browse and download SG Office product catalogues for stationery, arts & crafts, educational resources, sports, and more across the UAE.",
    path: "/catalogue",
  },
  quote: {
    title: "Request a Bulk Quote | Office & School Supplies UAE | SG Office",
    description:
      "Get a custom quote for bulk office supplies, school stationery, signage, or printing. Fast response for businesses and institutions across the UAE.",
    path: "/quote",
  },
  contact: {
    title: "Contact SG Office Supplies | Dubai, UAE",
    description:
      "Contact SG Office in Al Quoz, Dubai. Call 050 934 8007, email sales@sgoffice.me, or message us on WhatsApp for office and school supply enquiries.",
    path: "/contact",
  },
  orderTracking: {
    title: "Track Your Order | SG Office Supplies UAE",
    description:
      "Track your SG Office order status using your order number and email. Delivery across the United Arab Emirates.",
    path: "/order-tracking",
  },
  cart: {
    title: "Your Cart | SG Office Supplies",
    description: "Review items in your cart and proceed to checkout.",
    path: "/cart",
    noindex: true,
  },
  checkout: {
    title: "Checkout | SG Office Supplies",
    description: "Complete your order with delivery details across the UAE.",
    path: "/checkout",
    noindex: true,
  },
  wishlist: {
    title: "Wishlist | SG Office Supplies",
    description: "Your saved products at SG Office Supplies.",
    path: "/wishlist",
    noindex: true,
  },
  orders: {
    title: "My Orders | SG Office Supplies",
    description: "View your order history with SG Office Supplies.",
    path: "/orders",
    noindex: true,
  },
  login: {
    title: "Login | SG Office Supplies",
    description: "Sign in to your SG Office account.",
    path: "/login",
    noindex: true,
  },
  register: {
    title: "Register | SG Office Supplies",
    description: "Create your SG Office account.",
    path: "/register",
    noindex: true,
  },
  forgotPassword: {
    title: "Forgot Password | SG Office Supplies",
    description: "Request a secure password reset link for your SG Office account.",
    path: "/forgot-password",
    noindex: true,
  },
  resetPassword: {
    title: "Reset Password | SG Office Supplies",
    description: "Set a new password for your SG Office account.",
    path: "/reset-password",
    noindex: true,
  },
};

/** Category slug → SEO copy */
export const CATEGORY_SEO = {
  "files-folders": {
    title: "Files & Folders UAE | Buy Online | SG Office Supplies",
    description:
      "Shop files and folders for schools, offices, and businesses in the UAE. Quality products, competitive prices, and delivery across Dubai and the Emirates.",
  },
  "writing-instruments": {
    title: "Writing Instruments Dubai | Pens, Markers & More | SG Office",
    description:
      "Shop pens, pencils, markers, and writing essentials for schools and offices across the UAE. Bulk ordering and fast delivery from SG Office Supplies.",
  },
  "office-equipment": {
    title: "Office Equipment Supplier UAE | SG Office Supplies",
    description:
      "Office equipment for workplaces and institutions in Dubai and the UAE. Order online with reliable delivery from SG Office Supplies.",
  },
  "office-machinery": {
    title: "Office Machinery UAE | SG Office Supplies",
    description:
      "Office machinery and equipment for businesses across the United Arab Emirates. Shop online at SG Office Supplies.",
  },
  "early-years-primary-secondary": {
    title: "School Supplies UAE | Primary & Secondary | SG Office",
    description:
      "Educational supplies for primary and secondary schools across the UAE. Classroom essentials with bulk quote options available.",
  },
  "sports-leisure": {
    title: "Sports & Leisure Supplies UAE | SG Office Supplies",
    description:
      "Sports and leisure products for schools and organizations in the UAE. Shop online with delivery across Dubai and the Emirates.",
  },
  "art-craft": {
    title: "Arts & Crafts Supplies UAE | School & Office | SG Office",
    description:
      "Arts and crafts supplies for schools, offices, and creative projects across the UAE. Order from SG Office Supplies.",
  },
};

export function getCategorySeo(slug, categoryName = "") {
  const preset = CATEGORY_SEO[slug];
  if (preset) return preset;

  if (!categoryName) return null;

  return {
    title: `${categoryName} UAE | Buy Online | SG Office Supplies`,
    description: `Shop ${categoryName} for schools, offices, and businesses in the UAE. Quality products, competitive prices, and delivery across Dubai and the Emirates. Order from SG Office Supplies.`,
  };
}

export function getProductSeo(product) {
  if (!product) return null;

  const categoryName =
    typeof product.category === "object"
      ? product.category?.name
      : product.category || "";

  const shortDesc = (product.description || "")
    .replace(/<[^>]+>/g, "")
    .trim()
    .slice(0, 140);

  const description =
    shortDesc ||
    `Buy ${product.name}${categoryName ? ` — ${categoryName}` : ""} online in the UAE. Order from SG Office Supplies with delivery across Dubai and the Emirates.`;

  return {
    title: `${product.name} | SG Office UAE`,
    description,
    path: `/product/${product.slug || product.id}`,
    image: product.images?.[0] || product.image || DEFAULT_OG_IMAGE,
  };
}

export function buildAbsoluteUrl(path = "/") {
  const base = SITE_URL.replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}
